// F-V: "Generate Narration Audio" entry point (Exhibits batch bar). Disabled with the reason as tooltip when a run
// is already going, an admin switched AI narration off, the month's budget
// is used up, or (when the caller knows) nothing is missing.

import { useState, type FC } from "react";
import { Mic } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { useAiUsage } from "@shared/api/hooks/useAiUsage";
import type { NarrationKind } from "@shared/api/narration";
import { useNarrationRun } from "./NarrationRunContext";
import NarrationPlanDialog from "./NarrationPlanDialog";

export interface GenerateNarrationButtonProps {
  kind: NarrationKind;
  ids: string[];
  /** Exhibits with at least one missing slot; omit when unknown. */
  missingCount?: number;
  size?: "sm" | "default";
  variant?: "default" | "outline";
  testId: string;
}

export function narrationDisabledReason(opts: {
  isRunning: boolean;
  generationEnabled: boolean | undefined;
  blocked: boolean | undefined;
  missingCount: number | undefined;
}): string | null {
  if (opts.isRunning) return "A narration run is in progress";
  if (opts.generationEnabled === false)
    return "AI narration is switched off by an admin";
  if (opts.blocked) return "This month's AI narration budget is used up";
  if (opts.missingCount === 0)
    return "All selected exhibits already have narration audio";
  return null;
}

const GenerateNarrationButton: FC<GenerateNarrationButtonProps> = ({
  kind,
  ids,
  missingCount,
  size = "sm",
  variant = "outline",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const { isRunning } = useNarrationRun();
  const { data: usage } = useAiUsage();
  const reason = narrationDisabledReason({
    isRunning,
    generationEnabled: usage?.generationEnabled,
    blocked: usage?.blocked,
    missingCount,
  });
  return (
    <>
      <span title={reason ?? undefined}>
        <Button
          type="button"
          size={size}
          variant={variant}
          disabled={Boolean(reason)}
          onClick={() => setOpen(true)}
          data-testid={testId}
        >
          <Mic className="w-4 h-4 mr-1" />
          Generate Narration Audio
          {missingCount !== undefined ? ` (${missingCount})` : ""}
        </Button>
      </span>
      {open && (
        <NarrationPlanDialog
          open={open}
          onOpenChange={setOpen}
          kind={kind}
          ids={ids}
        />
      )}
    </>
  );
};

export default GenerateNarrationButton;
